// The Feedback panel (header button "Feedback"): the person describes a bug, an
// idea or a question; the AI asks at most one short question per turn or
// shows a draft issue; Send files it (a support ticket, and a GitHub issue
// when the server has one set up). When the AI is not available, the person
// can still send their own words as they are.
// Built from the design system's docked Sheet and Chat parts, like Ask.
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useLocation } from "react-router";
import type { FeedbackDraft, FeedbackDraftResult, FeedbackIssueResult, FeedbackKind, FeedbackMessage } from "@convenor/shared/v3";
import { api, isApiError } from "../../api/client";
import { Button, Notice, Tag } from "../../ui";

type Entry = { key: string; role: "user" | "assistant"; text: string; draft?: FeedbackDraft };

const KIND_LABEL: Record<FeedbackKind, string> = { bug: "Bug", idea: "Idea", question: "Question" };
const UUID = /^\/p\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(\/|$)/i;

/** A draft as text, for the AI's next turn and the transcript. */
const draftText = (d: FeedbackDraft) => [`Draft (${d.kind}): ${d.title}`, d.problem, ...d.details.map((x) => `- ${x}`)].join("\n");
const toMessage = (e: Entry): FeedbackMessage => ({ role: e.role, text: (e.draft ? draftText(e.draft) : e.text).slice(0, 4000) });

/** The person's own words as a draft (the AI is not available). */
export function ownWordsDraft(entries: Entry[]): FeedbackDraft {
  const words = entries
    .filter((e) => e.role === "user")
    .map((e) => e.text)
    .join("\n\n")
    .trim();
  const first = words.split("\n")[0]!.trim();
  return { kind: "bug", title: first.length > 120 ? `${first.slice(0, 119)}…` : first, problem: words.slice(0, 1000), details: [] };
}

export function FeedbackPanel({ projectId, onClose }: { projectId?: string | null; onClose: () => void }) {
  const location = useLocation();
  const fromPath = `${location.pathname}${location.search}`;
  const project = projectId ?? UUID.exec(location.pathname)?.[1] ?? null;
  const [entries, setEntries] = useState<Entry[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<FeedbackIssueResult | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const body = useRef<HTMLDivElement>(null);

  const close = useRef(onClose);
  close.current = onClose;

  // Esc closes the panel; on close, focus goes back to the Feedback button.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") close.current();
    };
    document.addEventListener("keydown", onKey);
    box.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      opener?.focus();
    };
  }, []);

  useEffect(() => {
    const el = body.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries, sent]);

  const last = entries[entries.length - 1];
  const draft = last?.draft && !changing ? last.draft : null;

  const ask = async () => {
    const said = text.trim();
    if (!said || busy) return;
    const next: Entry[] = [...entries, { key: `u${Date.now()}`, role: "user", text: said }];
    setEntries(next);
    setText("");
    setChanging(false);
    setError(null);
    setBusy(true);
    try {
      const res = await api.post<FeedbackDraftResult>("/api/product-feedback/draft", { messages: next.slice(-20).map(toMessage), fromPath });
      setEntries((all) => [...all, res.draft ? { key: `a${Date.now()}`, role: "assistant", text: "", draft: res.draft } : { key: `a${Date.now()}`, role: "assistant", text: res.reply ?? "" }]);
    } catch (e) {
      if (isApiError(e) && (e.code === "AI_UNAVAILABLE" || e.status === 503)) setUnavailable(e.message);
      else setError(isApiError(e) ? e.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
      box.current?.focus();
    }
  };

  const send = async (d: FeedbackDraft) => {
    setBusy(true);
    setError(null);
    try {
      setSent(
        await api.post<FeedbackIssueResult>("/api/product-feedback/issue", {
          draft: d,
          fromPath,
          projectId: project,
          transcript: entries.slice(-40).map(toMessage),
        }),
      );
    } catch (e) {
      setError(isApiError(e) ? e.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const change = () => {
    setChanging(true);
    box.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void ask();
    }
  };

  const hasWords = entries.some((e) => e.role === "user");

  return (
    <aside className="sheet sheet--docked" aria-label="Feedback">
      <header className="sheet__head">
        <h2 className="sheet__title">Feedback</h2>
        <button type="button" className="sheet__close" onClick={onClose}>
          <span aria-hidden="true">×</span>
          <span className="visually-hidden">Close feedback</span>
        </button>
      </header>

      <div className="sheet__body" ref={body}>
        <div className="chat">
          {entries.length ? (
            <ol className="chat__log">
              {entries.map((m) => (
                <li key={m.key} className={`chat__message${m.role === "user" ? " chat__message--person" : ""}`}>
                  {m.draft ? <DraftCard draft={m.draft} /> : <p className="chat__bubble">{m.text}</p>}
                </li>
              ))}
              {busy && !sent ? (
                <li className="chat__message">
                  <p className="chat__busy">Working…</p>
                </li>
              ) : null}
            </ol>
          ) : (
            <p className="chat__empty">Tell us about a bug, an idea or something that confused you. We turn it into a short report for the product team.</p>
          )}
        </div>
        <div aria-live="polite">
          {sent ? (
            <Notice kind="success" title="Thank you.">
              {sent.issueNumber !== null ? (
                sent.issueUrl ? (
                  <p>
                    Logged as{" "}
                    <a href={sent.issueUrl} target="_blank" rel="noreferrer">
                      #{sent.issueNumber}
                    </a>
                    .
                  </p>
                ) : (
                  <p>Logged as #{sent.issueNumber}.</p>
                )
              ) : (
                <p>Sent to support.</p>
              )}
            </Notice>
          ) : null}
          {error ? <Notice kind="warning">{error}</Notice> : null}
        </div>
      </div>

      <footer className="sheet__foot">
        {sent ? (
          <div className="chat__send">
            <Button onClick={onClose}>Close</Button>
          </div>
        ) : unavailable ? (
          <Notice title={unavailable}>
            {hasWords ? (
              <div className="chat__send">
                <Button busy={busy} busyLabel="Sending…" onClick={() => void send(ownWordsDraft(entries))}>
                  Send as it is
                </Button>
              </div>
            ) : null}
          </Notice>
        ) : draft ? (
          <div className="chat__send">
            <Button variant="text" onClick={change} disabled={busy}>
              Change something
            </Button>
            <Button busy={busy} busyLabel="Sending…" onClick={() => void send(draft)}>
              Send
            </Button>
          </div>
        ) : (
          <form
            className="chat__composer"
            onSubmit={(e) => {
              e.preventDefault();
              void ask();
            }}
          >
            <label htmlFor="feedback-text">{changing ? "What should change?" : entries.length ? "Your answer" : "What would you like to tell us?"}</label>
            <textarea id="feedback-text" className="chat__box" ref={box} rows={3} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown} readOnly={busy} maxLength={4000} />
            <div className="chat__send">
              <Button type="submit" busy={busy} busyLabel="Working…">
                Next
              </Button>
            </div>
          </form>
        )}
      </footer>
    </aside>
  );
}

function DraftCard({ draft }: { draft: FeedbackDraft }) {
  return (
    <section className="card chat__card" aria-label="Draft report">
      <p>
        <Tag tone="info">{KIND_LABEL[draft.kind]}</Tag>
      </p>
      <h3 className="card__title">{draft.title}</h3>
      <p className="card__text">{draft.problem}</p>
      {draft.details.length ? (
        <ul>
          {draft.details.map((d, i) => (
            <li key={i}>{d}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
